import React, { useEffect, useRef } from "react";
import { useUIEventBusContext } from "@codenexus/react";
import { getHostDocumentBridge } from "./HostDocumentBridgeProvider";

/**
 * Watches the host-document adapter for the currently-active document and
 * keeps the agent **passively aware** of section changes — without triggering
 * a chat turn.
 *
 * Mechanism: when the active section changes, push a `host_navigation` note
 * onto the UI event bus via `pushContext`. The note rides on the *next* user
 * message via ChatProvider.send's pre-send drain, so the agent sees the
 * switch as ambient context the next time the user actually asks something.
 *
 * What this watcher used to do (and DOES NOT do anymore):
 *   We previously also called `fireSkill("host-documents", …)` on every doc
 *   change. That generated an unsolicited backend POST /api/chat, the model
 *   would stream a response (sometimes a degenerate refusal loop) every time
 *   the user navigated tabs, and the synthetic activation prime polluted the
 *   conversation history. The agent loads the host-documents skill on its own
 *   when the user references the doc — the system-prompt hint covers that —
 *   so the proactive fireSkill was redundant *and* harmful.
 *
 * Fires (pushContext) on:
 *   • first registration (no doc → some doc)
 *   • doc swap (docId A → docId B)
 *
 * Does NOT fire on:
 *   • doc unmount (doc → no doc) — keep prior context
 *   • content edits within the same doc — typing should not interrupt the chat
 */
export const DocumentContextWatcher: React.FC = () => {
    const bus = useUIEventBusContext();
    const lastDocRef = useRef<{ id: string; title: string } | null>(null);
    const lastPushedDocRef = useRef<{ id: string; title: string } | null>(null);

    useEffect(() => {
        const { composite: adapter } = getHostDocumentBridge();

        const handle = (refs: Array<{ id: string; title: string; active?: boolean }>) => {
            const currentRef = refs.find((ref) => ref.active) ?? null;
            const previous = lastDocRef.current;

            if (!currentRef) {
                // Keep prior doc through transient unmounts/remounts so we
                // don't repeatedly push the same "opened/switched" context.
                return;
            }
            if (currentRef.id === previous?.id) return;
            lastDocRef.current = { id: currentRef.id, title: currentRef.title };

            const title = currentRef.title ?? "";

            // Idempotency guard: if the same doc/title was already pushed most
            // recently, skip duplicate context pushes.
            if (lastPushedDocRef.current?.id === currentRef.id && lastPushedDocRef.current?.title === title) {
                return;
            }

            console.log(
                "[DocumentContextWatcher] active doc changed (passive context push)",
                { docId: currentRef.id, title, previous }
            );

            if (bus) {
                const navigationNote = previous
                    ? `Operator switched the active editor section from "${previous.title}" to "${title}". Orient subsequent edits and discussion to "${title}" unless the operator explicitly references another section.`
                    : `Operator opened section "${title}" in the editor. Orient subsequent edits and discussion to this section.`;
                bus.pushContext({
                    host_navigation: {
                        event: previous ? "editor_section_switched" : "editor_section_opened",
                        from: previous,
                        to: { id: currentRef.id, title },
                        note: navigationNote
                    }
                });
                lastPushedDocRef.current = { id: currentRef.id, title };
            }
        };

        handle(adapter.listOpen());
        const unsubscribe = adapter.onChange(handle);
        return unsubscribe;
    }, [bus]);

    return null;
};
